import {
	enableProdMode,
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
	provideEnvironmentInitializer,
	provideZoneChangeDetection,
} from "@angular/core";
import * as Sentry from "@sentry/angular";
import { TraceService } from "@sentry/angular";
import { tokenGetter } from "./app/app.module";
import { environment } from "./environments/environment";
import { AppComponent } from "./app/app/app.component";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatBadgeModule } from "@angular/material/badge";
import { MatTabsModule } from "@angular/material/tabs";
import { MatRadioModule } from "@angular/material/radio";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatMenuModule } from "@angular/material/menu";
import { MatStepperModule } from "@angular/material/stepper";
import { MatSelectModule } from "@angular/material/select";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDialogModule } from "@angular/material/dialog";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatChipsModule } from "@angular/material/chips";
import { MatCardModule } from "@angular/material/card";
import { MatSortModule } from "@angular/material/sort";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatTableModule } from "@angular/material/table";
import { JwtModule } from "@auth0/angular-jwt";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatListModule } from "@angular/material/list";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatSidenavModule } from "@angular/material/sidenav";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { bootstrapApplication, BrowserModule } from "@angular/platform-browser";
import { provideRouter, Router, withPreloading } from "@angular/router";
import { SelectivePreloadStrategy } from "./app/services/selective-preload.strategy";
import { provideNgxMask } from "ngx-mask";
import { DatePipe } from "@angular/common";
import { GroupByPipe, NgPipesModule, OrderByPipe, PercentagePipe, RoundPipe, UniquePipe } from "ngx-pipes";
import { MAT_DATE_LOCALE, MatNativeDateModule, MatOptionModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { EditorModule, TINYMCE_SCRIPT_SRC } from "@tinymce/tinymce-angular";
import { MitpManagerService } from "./app/services/mitp-manager.service";
import { HttpInterceptorService } from "./app/services/http-interceptor.service";
import {
	HTTP_INTERCEPTORS,
	HttpErrorResponse,
	provideHttpClient,
	withInterceptorsFromDi,
	withXhr,
} from "@angular/common/http";
import { AssetsService } from "./app/services/assets.service";
import { HiddenFeaturesService } from "./app/services/hidden-features.service";
import { DevTools, FormatSimple, LanguageDetector, LanguageStorage, provideTolgee, Tolgee } from "@tolgee/ngx";
import { NamePipe } from "./app/pipes/name.pipe";
import { provideEchartsCore } from "ngx-echarts";
import {
	BarChart,
	GraphChart,
	HeatmapChart,
	LineChart,
	PieChart,
	RadarChart,
	SankeyChart,
	ScatterChart,
} from "echarts/charts";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import {
	GraphicComponent,
	GridComponent,
	LegendComponent,
	TitleComponent,
	ToolboxComponent,
	TooltipComponent,
	VisualMapComponent,
} from "echarts/components";
import { ShortcutTitlePipe } from "./app/pipes/shortcut-title.pipe";
import { appRoutes } from "./app/app.routes";
import { SNOMED_CONFIG } from "./app/modules/core/snomed/snomed.config";
import { BytesSizePipe } from "./app/pipes/bytes-size.pipe";
import { OVERLAY_DEFAULT_CONFIG } from "@angular/cdk/overlay";

echarts.use([
	BarChart,
	PieChart,
	LineChart,
	GraphChart,
	GridComponent,
	GraphicComponent,
	LegendComponent,
	CanvasRenderer,
	TooltipComponent,
	ToolboxComponent,
	RadarChart,
	TitleComponent,
	ScatterChart,
	HeatmapChart,
	VisualMapComponent,
	SankeyChart,
]);

const IGNORED_HTTP_STATUS_CODES = new Set([400, 401, 403, 404]);

Sentry.init({
	dsn: "https://289becec72c61804fc0aa85c486986c9@o4507374080360448.ingest.de.sentry.io/4507374091239504",
	integrations: [],
	enabled: environment.production,
	release: environment.appVersion,
	beforeSend(event, hint) {
		const error = hint?.originalException ?? hint?.syntheticException;
		const potentialStatus =
			error instanceof HttpErrorResponse ? error.status : (error as { status?: unknown } | undefined)?.status;
		const status = typeof potentialStatus === "number" ? potentialStatus : undefined;

		if (status !== undefined && IGNORED_HTTP_STATUS_CODES.has(status)) {
			return null;
		}

		return event;
	},
});

if (environment.production) {
	enableProdMode();
}

bootstrapApplication(AppComponent, {
	providers: [
		importProvidersFrom(
			BrowserModule,
			FormsModule,
			MatSidenavModule,
			MatToolbarModule,
			MatListModule,
			MatButtonModule,
			MatIconModule,
			MatFormFieldModule,
			MatInputModule,
			ReactiveFormsModule,
			JwtModule.forRoot({
				config: {
					tokenGetter: tokenGetter,
					allowedDomains: ["api.medinthepocket.com", "localhost:8000"],
					// The patient portal authenticates with its own emailed token. A practitioner
					// JWT left in the session storage must never be attached to those calls, and
					// would otherwise overwrite the Authorization header set by the portal service.
					disallowedRoutes: [/\/patient-portal(\/|$)/],
				},
			}),
			MatTableModule,
			MatPaginatorModule,
			MatSortModule,
			MatCardModule,
			MatChipsModule,
			MatOptionModule,
			MatAutocompleteModule,
			MatDialogModule,
			MatProgressSpinnerModule,
			MatExpansionModule,
			MatSelectModule,
			MatStepperModule,
			MatMenuModule,
			EditorModule,
			MatCheckboxModule,
			MatRadioModule,
			MatTabsModule,
			MatBadgeModule,
			MatDatepickerModule,
			MatNativeDateModule,
			MatTooltipModule,
			MatSlideToggleModule,
			NgPipesModule
		),
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpInterceptorService,
			multi: true,
		},
		{ provide: Window, useValue: window },
		MitpManagerService,
		{ provide: TINYMCE_SCRIPT_SRC, useValue: "tinymce/tinymce.min.js" },
		MatNativeDateModule,
		{ provide: MAT_DATE_LOCALE, useValue: "fr-FR" },
		UniquePipe,
		OrderByPipe,
		NamePipe,
		BytesSizePipe,
		ShortcutTitlePipe,
		PercentagePipe,
		DatePipe,
		GroupByPipe,
		RoundPipe,
		provideNgxMask(),
		provideZoneChangeDetection(),
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false,
			}),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{ provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
		provideAppInitializer(() => {
			inject(TraceService);
		}),
		// Registers the `mitp` console commands that reveal hidden features
		provideAppInitializer(() => {
			inject(HiddenFeaturesService);
		}),
		provideRouter(appRoutes, withPreloading(SelectivePreloadStrategy)),
		provideHttpClient(withXhr(), withInterceptorsFromDi()),
		provideEnvironmentInitializer(() => inject(AssetsService).init()),
		provideTolgee(() => {
			return Tolgee()
				.use(DevTools())
				.use(FormatSimple())
				.use(LanguageStorage())
				.use(LanguageDetector())
				.init({
					defaultLanguage: "en",
					// for development
					apiUrl: environment.tolgeeApiUrl,
					apiKey: environment.tolgeeApiKey,
					staticData: {
						fr: () => import("./assets/i18n/fr.json").then((m) => m.default),
						en: () => import("./assets/i18n/en.json").then((m) => m.default),
						de: () => import("./assets/i18n/de.json").then((m) => m.default),
						vi: () => import("./assets/i18n/vi.json").then((m) => m.default),
						ar: () => import("./assets/i18n/ar.json").then((m) => m.default),
						es: () => import("./assets/i18n/es.json").then((m) => m.default),
						pt: () => import("./assets/i18n/pt.json").then((m) => m.default),
					},
				});
		}),
		{
			provide: SNOMED_CONFIG,
			useValue: {
				baseUrl: "https://snowstorm.ihtsdotools.org/snowstorm",
				branch: "MAIN",
				fallback: "—",
			},
		},
		provideEchartsCore({ echarts }),
	],
})
	.then(() => {
		const preloader = document.getElementById("preloader");
		if (preloader) {
			preloader.style.opacity = "0";
			setTimeout(() => preloader.remove(), 300);
		}
	})
	.catch((err) => console.error(err));
